/**
 * @voltxampere/engine — Assertion DSL parser (Sprint 9)
 *
 * Recursive-descent parser that consumes the token stream from
 * {@link ./lexer.ts} and produces an {@link AssertionAST}. The
 * grammar is the EBNF in Blueprint §24.2 / SPECS §6. Operator
 * precedence — comparator > additive > multiplicative > primary — is realised
 * by four mutually-recursive methods mirroring each level.
 *
 * Parse errors throw {@link AssertionParseError} carrying the
 * source column so the i18n layer can point at the offending
 * token with a caret.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ANALYSIS_KINDS } from "./ast.js";
import { AssertionLexer, AssertionLexError } from "./lexer.js";
import { COMPARATOR_SYMBOLS } from "./tokens.js";
import { unitMultiplier } from "./units.js";
export class AssertionParseError extends Error {
    column;
    tokenValue;
    constructor(message, column, tokenValue) {
        super(`${message} (column ${String(column)})`);
        this.name = "AssertionParseError";
        this.column = column;
        this.tokenValue = tokenValue;
    }
}
export class AssertionParser {
    tokens = [];
    pos = 0;
    parse(input, options = {}) {
        try {
            this.tokens = new AssertionLexer().tokenize(input, options.locale !== undefined ? { locale: options.locale } : {});
        }
        catch (err) {
            if (err instanceof AssertionLexError) {
                // Sprint 10.0 (A-P1-01 closure): the AssertionLexError
                // constructor appends " (column N)" to its message, and the
                // AssertionParseError constructor below would append another
                // " (column N)". Strip the lexer's suffix before re-wrapping
                // so the rendered .message contains exactly one column
                // annotation.
                const stripped = err.message.replace(/ \(column \d+\)$/, "");
                throw new AssertionParseError(stripped, err.column, err.char);
            }
            throw err;
        }
        this.pos = 0;
        const ast = this.parseAssertion();
        this.expect("EOF", "end of input");
        return ast;
    }
    // -----------------------------------------------------------
    // Top-level assertion
    // -----------------------------------------------------------
    parseAssertion() {
        this.consumeKeyword("assertion", "'assert' or 'iddia'");
        const left = this.parseExpression();
        // Oscillation shorthand: `<expr> oscillates <freq> [± <tol>] [source=<id>]`
        if (this.matchKeyword("oscillates")) {
            this.advance();
            const freqLit = this.expectNumberWithOptionalUnit();
            let tolerance = 0;
            let toleranceUnit;
            if (this.peek().kind === "TOLERANCE") {
                this.advance();
                const tolLit = this.expectNumberWithOptionalUnit();
                tolerance = tolLit.value;
                toleranceUnit = tolLit.unit;
            }
            let sourceName;
            if (this.matchKeyword("source")) {
                this.advance();
                this.expect("EQUALS", "'='");
                const ident = this.expect("IDENTIFIER", "source component id");
                sourceName = ident.value;
            }
            const condition = this.tryParseCondition();
            return {
                kind: "oscillation",
                expr: left,
                freq: freqLit.value,
                ...(freqLit.unit !== undefined ? { freqUnit: freqLit.unit } : {}),
                tolerance,
                ...(toleranceUnit !== undefined ? { toleranceUnit } : {}),
                ...(sourceName !== undefined ? { source: sourceName } : {}),
                ...(condition !== undefined ? { condition } : {}),
            };
        }
        const compTok = this.expect("COMPARATOR", "comparator (<, >, ≤, ≥, ≈, ==, !=)");
        const op = this.comparatorSymbol(compTok);
        if (op === "≈") {
            // Tolerance form: `≈ center ± tolerance`
            const center = this.expectNumberWithOptionalUnit();
            let tolerance = 0;
            let toleranceKind = "abs";
            let toleranceUnit;
            if (this.peek().kind === "TOLERANCE") {
                this.advance();
                const tol = this.parsePercentOrUnit();
                tolerance = tol.value;
                toleranceKind = tol.kind;
                toleranceUnit = tol.unit;
            }
            const condition = this.tryParseCondition();
            return {
                kind: "tolerance",
                expr: left,
                center: center.value,
                ...(center.unit !== undefined ? { centerUnit: center.unit } : {}),
                tolerance,
                toleranceKind,
                ...(toleranceUnit !== undefined ? { toleranceUnit } : {}),
                ...(condition !== undefined ? { condition } : {}),
            };
        }
        const right = this.parseExpression();
        const condition = this.tryParseCondition();
        return {
            kind: "binary",
            op,
            left,
            right,
            ...(condition !== undefined ? { condition } : {}),
        };
    }
    // -----------------------------------------------------------
    // Expression — additive > multiplicative > unary > primary
    // -----------------------------------------------------------
    parseExpression() {
        let left = this.parseTerm();
        for (;;) {
            const tok = this.peek();
            if (tok.kind === "OPERATOR" && (tok.value === "+" || tok.value === "-")) {
                this.advance();
                const right = this.parseTerm();
                left = { kind: "binary", op: tok.value, left, right };
            }
            else {
                break;
            }
        }
        return left;
    }
    parseTerm() {
        let left = this.parseFactor();
        for (;;) {
            const tok = this.peek();
            if (tok.kind === "OPERATOR" && (tok.value === "*" || tok.value === "/")) {
                this.advance();
                const right = this.parseFactor();
                left = { kind: "binary", op: tok.value, left, right };
            }
            else {
                break;
            }
        }
        return left;
    }
    parseFactor() {
        const tok = this.peek();
        if (tok.kind === "OPERATOR" && tok.value === "-") {
            // Unary minus: fold into a literal when possible so the
            // evaluator does not have to special-case negation.
            this.advance();
            const inner = this.parseFactor();
            if (inner.kind === "literal") {
                return { ...inner, value: -inner.value };
            }
            return {
                kind: "binary",
                op: "-",
                left: { kind: "literal", value: 0 },
                right: inner,
            };
        }
        if (tok.kind === "LPAREN") {
            this.advance();
            const expr = this.parseExpression();
            this.expect("RPAREN", "')'");
            return expr;
        }
        if (tok.kind === "NUMBER") {
            return this.readNumberAsLiteral();
        }
        if (tok.kind === "IDENTIFIER") {
            return this.parseIdentifierFactor();
        }
        throw new AssertionParseError(`Expected expression, got '${tok.value}'`, tok.position, tok.value);
    }
    parseIdentifierFactor() {
        const id = this.expect("IDENTIFIER", "identifier");
        const next = this.peek();
        if (next.kind !== "LPAREN") {
            // Bare identifier in expression position is a net name
            // used outside a V()/I() wrapper — allowed as shorthand
            // for V(identifier).
            return { kind: "nodeRef", net: id.value };
        }
        this.advance(); // consume '('
        const args = [];
        if (this.peek().kind !== "RPAREN") {
            args.push(this.parseExpression());
            while (this.peek().kind === "COMMA") {
                this.advance();
                args.push(this.parseExpression());
            }
        }
        this.expect("RPAREN", "')'");
        // Special-case V() and I() so the AST carries the
        // semantically-correct node/component references rather than
        // opaque function calls.
        if (id.value === "V") {
            if (args.length !== 1 || args[0]?.kind !== "nodeRef") {
                throw new AssertionParseError("V() takes exactly one node identifier", id.position, id.value);
            }
            return { kind: "nodeRef", net: args[0].net };
        }
        if (id.value === "I") {
            if (args.length !== 1) {
                throw new AssertionParseError("I() takes exactly one component identifier", id.position, id.value);
            }
            const inner = args[0];
            if (inner?.kind === "nodeRef") {
                return { kind: "componentRef", id: inner.net, property: "I" };
            }
            throw new AssertionParseError("I() argument must be a component identifier", id.position, id.value);
        }
        return { kind: "function", name: id.value, args };
    }
    readNumberAsLiteral() {
        const numTok = this.expect("NUMBER", "number");
        const value = Number(numTok.value);
        if (!Number.isFinite(value)) {
            throw new AssertionParseError(`Invalid numeric literal '${numTok.value}'`, numTok.position, numTok.value);
        }
        const unitTok = this.peek();
        if (unitTok.kind === "UNIT") {
            this.advance();
            const scaled = value * unitMultiplier(unitTok.value);
            return { kind: "literal", value: scaled, unit: unitTok.value };
        }
        if (unitTok.kind === "PERCENT") {
            this.advance();
            return { kind: "literal", value: value * 0.01, unit: "%" };
        }
        return { kind: "literal", value };
    }
    // -----------------------------------------------------------
    // Conditions and helpers
    // -----------------------------------------------------------
    tryParseCondition() {
        const tok = this.peek();
        if (tok.kind === "KEYWORD" && tok.value === "allTimes") {
            this.advance();
            return { kind: "allTimes" };
        }
        if (tok.kind === "KEYWORD" && tok.value === "conditionAt") {
            this.advance();
            this.expect("EQUALS", "'='");
            const analysis = this.expectAnalysisKind();
            return { kind: "at", analysis };
        }
        if (tok.kind === "KEYWORD" && tok.value === "conditionWhen") {
            this.advance();
            if (this.matchKeyword("simEqualsAlias"))
                this.advance();
            this.expect("EQUALS", "'='");
            const analysis = this.expectAnalysisKind();
            return { kind: "when", analysis };
        }
        return undefined;
    }
    expectAnalysisKind() {
        const tok = this.expect("IDENTIFIER", "analysis kind (DC, TRANSIENT, AC)");
        const upper = tok.value.toUpperCase();
        if (!ANALYSIS_KINDS.includes(upper)) {
            throw new AssertionParseError(`Unknown analysis kind '${tok.value}'. Expected one of DC, TRANSIENT, AC.`, tok.position, tok.value);
        }
        return upper;
    }
    expectNumberWithOptionalUnit() {
        const numTok = this.expect("NUMBER", "numeric value");
        const n = Number(numTok.value);
        const unitTok = this.peek();
        if (unitTok.kind === "UNIT") {
            this.advance();
            return { value: n * unitMultiplier(unitTok.value), unit: unitTok.value };
        }
        return { value: n };
    }
    parsePercentOrUnit() {
        // `±%1` form (PERCENT then NUMBER)
        if (this.peek().kind === "PERCENT") {
            this.advance();
            const numTok = this.expect("NUMBER", "percent value");
            return { value: Number(numTok.value) * 0.01, kind: "pct", unit: "%" };
        }
        // `±1%` form or `±0.1V` form
        const numTok = this.expect("NUMBER", "tolerance value");
        const rawValue = Number(numTok.value);
        const next = this.peek();
        if (next.kind === "PERCENT") {
            this.advance();
            return { value: rawValue * 0.01, kind: "pct", unit: "%" };
        }
        if (next.kind === "UNIT") {
            this.advance();
            return { value: rawValue * unitMultiplier(next.value), kind: "abs", unit: next.value };
        }
        return { value: rawValue, kind: "abs" };
    }
    comparatorSymbol(tok) {
        if (COMPARATOR_SYMBOLS.includes(tok.value)) {
            return tok.value;
        }
        throw new AssertionParseError(`Unknown comparator '${tok.value}'`, tok.position, tok.value);
    }
    // -----------------------------------------------------------
    // Token stream helpers
    // -----------------------------------------------------------
    peek() {
        const t = this.tokens[this.pos];
        if (t === undefined) {
            throw new AssertionParseError("Unexpected end of token stream", this.tokens[this.tokens.length - 1]?.position ?? 0, "");
        }
        return t;
    }
    advance() {
        const t = this.peek();
        this.pos += 1;
        return t;
    }
    matchKeyword(canonical) {
        const tok = this.peek();
        return tok.kind === "KEYWORD" && tok.value === canonical;
    }
    consumeKeyword(canonical, expectationDescription) {
        const tok = this.peek();
        if (tok.kind !== "KEYWORD" || tok.value !== canonical) {
            throw new AssertionParseError(`Expected ${expectationDescription}, got '${tok.value}'`, tok.position, tok.value);
        }
        return this.advance();
    }
    expect(kind, expectationDescription) {
        const tok = this.peek();
        if (tok.kind !== kind) {
            throw new AssertionParseError(`Expected ${expectationDescription}, got '${tok.value || tok.kind.toLowerCase()}'`, tok.position, tok.value);
        }
        return this.advance();
    }
}
/** One-shot entry. */
export function parseAssertion(input, options) {
    return new AssertionParser().parse(input, options);
}
//# sourceMappingURL=parser.js.map